/**
 * Состояние чата и WebSocket.
 *
 * История приходит целиком при hello: старше 12 часов сервер уже не держит,
 * поэтому клиент не пытается догружать «ещё».
 */

import { create } from "zustand";

import {
  CHAT_LOBBY_SLUG,
  type ChatClientMessage,
  type ChatFx,
  type ChatMeDto,
  type ChatMessageDto,
  type ChatMuteDto,
  type ChatMuteDuration,
  type ChatPresenceDto,
  type ChatRoomDto,
  type ChatServerMessage,
} from "@koshkodrop/shared";

import { tokens } from "../lib/api";
import { haptic } from "../lib/telegram";
import { useSession } from "./session";

type Connection = "idle" | "connecting" | "open" | "closed";

export const CHAT_LAST_READ_KEY = "koshkodrop.chat.lastRead";

export function markChatRead(): void {
  localStorage.setItem(CHAT_LAST_READ_KEY, String(Date.now()));
}

interface ChatState {
  connection: Connection;
  open: boolean;
  unread: number;
  room: ChatRoomDto | null;
  messages: ChatMessageDto[];
  online: ChatPresenceDto[];
  me: ChatMeDto | null;
  mutes: ChatMuteDto[];
  typing: { userId: string; firstName: string; until: number } | null;
  fx: { kind: ChatFx; nonce: number } | null;
  lastError: string | null;
  replyTo: ChatMessageDto | null;

  connect: (slug?: string) => void;
  disconnect: () => void;
  setOpen: (open: boolean) => void;
  send: (kind: "text" | "sticker", body: string) => void;
  react: (messageId: string, emoji: string) => void;
  typingNow: () => void;
  remove: (messageId: string) => void;
  mute: (userId: string, duration: ChatMuteDuration) => void;
  unmute: (userId: string) => void;
  setSlow: (seconds: number) => void;
  setReply: (message: ChatMessageDto | null) => void;
  clearError: () => void;
  clearFx: () => void;
}

let socket: WebSocket | null = null;
let reconnectTimer: number | null = null;
let pingTimer: number | null = null;
let reconnectAttempt = 0;
let activeSlug = CHAT_LOBBY_SLUG;
let stayConnected = false;

export const useChat = create<ChatState>((set, get) => {
  const sendRaw = (message: ChatClientMessage) => {
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
    }
  };

  const scheduleReconnect = () => {
    if (reconnectTimer !== null) return;
    const delay = Math.min(1000 * 2 ** reconnectAttempt, 15_000);
    reconnectAttempt += 1;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      get().connect(activeSlug);
    }, delay);
  };

  const applyFx = (kind: ChatFx | undefined) => {
    if (!kind || !get().open) return;
    set({ fx: { kind, nonce: Date.now() } });
    haptic.tap(kind === "purr" ? "heavy" : "medium");
  };

  const handleMessage = (message: ChatServerMessage) => {
    switch (message.type) {
      case "hello":
        set({
          room: message.room,
          messages: message.messages,
          online: message.online,
          me: message.me,
          mutes: message.mutes,
          lastError: null,
        });
        if (get().open) markChatRead();
        break;

      case "message": {
        const myId = useSession.getState().user?.id;
        const mine = message.message.author.userId === myId;
        set((state) => ({
          messages: state.messages.some((item) => item.id === message.message.id)
            ? state.messages
            : [...state.messages, message.message],
          unread: state.open || mine ? state.unread : state.unread + 1,
        }));
        applyFx(message.fx);
        if (get().open) markChatRead();
        break;
      }

      case "deleted":
        set((state) => ({
          messages: state.messages.filter((item) => item.id !== message.messageId),
          replyTo: state.replyTo?.id === message.messageId ? null : state.replyTo,
        }));
        break;

      case "reacted":
        set((state) => ({
          messages: state.messages.map((item) =>
            item.id === message.messageId ? { ...item, reactions: message.reactions } : item,
          ),
        }));
        applyFx(message.fx);
        break;

      case "typing": {
        const myId = useSession.getState().user?.id;
        if (message.userId === myId) break;
        set({ typing: { userId: message.userId, firstName: message.firstName, until: Date.now() + 2500 } });
        break;
      }

      case "presence":
        set({ online: message.online });
        break;

      case "muted":
        set((state) => ({
          me: state.me
            ? {
                ...state.me,
                mutedUntil: message.until && message.until !== "forever" ? message.until : null,
                mutedForever: message.until === "forever",
              }
            : state.me,
        }));
        if (message.until) haptic.error();
        else haptic.success();
        break;

      case "slow":
        set((state) => ({
          room: state.room ? { ...state.room, slowModeSeconds: message.seconds } : state.room,
        }));
        break;

      case "error":
        set({ lastError: message.message });
        haptic.error();
        break;

      case "user_muted": {
        const myId = useSession.getState().user?.id;
        set((state) => ({
          mutes: state.me?.canModerate
            ? message.mute
              ? [...state.mutes.filter((item) => item.userId !== message.userId), message.mute]
              : state.mutes.filter((item) => item.userId !== message.userId)
            : state.mutes,
          me:
            message.userId === myId && state.me
              ? {
                  ...state.me,
                  mutedUntil: message.mute?.forever ? null : message.mute?.until ?? null,
                  mutedForever: Boolean(message.mute?.forever),
                }
              : state.me,
        }));
        break;
      }
    }
  };

  return {
    connection: "idle",
    open: false,
    unread: 0,
    room: null,
    messages: [],
    online: [],
    me: null,
    mutes: [],
    typing: null,
    fx: null,
    lastError: null,
    replyTo: null,

    connect(slug = CHAT_LOBBY_SLUG) {
      activeSlug = slug;
      stayConnected = true;
      if (socket && (socket.readyState === 0 || socket.readyState === 1)) return;

      set({ connection: "connecting" });
      const protocol = location.protocol === "https:" ? "wss:" : "ws:";
      const token = tokens.access;
      const url = `${protocol}//${location.host}/api/chat/ws?room=${encodeURIComponent(slug)}${
        token ? `&token=${encodeURIComponent(token)}` : ""
      }`;

      socket = new WebSocket(url);

      socket.onopen = () => {
        reconnectAttempt = 0;
        set({ connection: "open" });
        pingTimer = window.setInterval(() => sendRaw({ type: "ping" }), 25_000);
      };

      socket.onmessage = (event: MessageEvent<string>) => {
        try {
          handleMessage(JSON.parse(event.data) as ChatServerMessage);
        } catch {
          // битый кадр не должен ронять чат
        }
      };

      socket.onclose = () => {
        if (pingTimer !== null) {
          window.clearInterval(pingTimer);
          pingTimer = null;
        }
        socket = null;
        set({ connection: "closed" });
        if (stayConnected) scheduleReconnect();
      };

      socket.onerror = () => {
        socket?.close();
      };
    },

    disconnect() {
      stayConnected = false;
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (pingTimer !== null) {
        window.clearInterval(pingTimer);
        pingTimer = null;
      }
      reconnectAttempt = 0;
      const current = socket;
      socket = null;
      current?.close();
      set({
        connection: "idle",
        typing: null,
        lastError: null,
        open: false,
      });
    },

    setOpen(open) {
      set({ open, unread: open ? 0 : get().unread });
      if (open) markChatRead();
    },

    send(kind, body) {
      const replyTo = get().replyTo;
      sendRaw({ type: "send", kind, body, replyToId: replyTo?.id ?? null });
      set({ replyTo: null });
      haptic.tap("light");
    },

    react(messageId, emoji) {
      sendRaw({ type: "react", messageId, emoji });
    },

    typingNow() {
      sendRaw({ type: "typing" });
    },

    remove(messageId) {
      sendRaw({ type: "delete", messageId });
    },

    mute(userId, duration) {
      sendRaw({ type: "mute", userId, duration });
    },

    unmute(userId) {
      sendRaw({ type: "unmute", userId });
    },

    setSlow(seconds) {
      sendRaw({ type: "slow", seconds });
    },

    setReply(message) {
      set({ replyTo: message });
    },

    clearError() {
      set({ lastError: null });
    },

    clearFx() {
      set({ fx: null });
    },
  };
});
